/**
 * Utility functions for filtering inbox transactions
 */

import { Transaction } from '@/types/api/payment';
import { User } from '@/types/api/user';

/**
 * Extended transaction type that includes all fields used in inbox filtering
 */
export interface InboxTransaction extends Transaction {
  isInvoice?: boolean;
  isOriginator?: boolean;
  isRedeemed?: boolean;
  isPaid?: boolean;
  userID?: string;
  userId?: string;
  username?: string;
  createdByCurrentUser?: boolean;
  createdBy?: string;
  originator?: string;
  senderName?: string;
  fromUser?: string;
  timestamp?: string;
  data?: {
    createdBy?: string;
    payload?: {
      escrowRequired?: boolean;
      escrow?: boolean;
      escrowSettings?: unknown;
      escrowReleased?: boolean;
      [key: string]: any;
    };
    [key: string]: any;
  };
  payload?: {
    createdBy?: string;
    order?: any;
    escrowRequired?: boolean;
    escrow?: boolean;
    escrowSettings?: unknown;
    escrowReleased?: boolean;
    [key: string]: any;
  };
  originatorPayload?: {
    escrowRequired?: boolean;
    escrow?: boolean;
    escrowSettings?: unknown;
    escrowReleased?: boolean;
    [key: string]: any;
  };
  escrowId?: string;
  escrowRequired?: boolean;
  escrowReleased?: boolean;
  escrowStatus?: string;
  redeemedAt?: string;
}

/**
 * Check if a transaction was created by the current user
 * @param tx Transaction to check
 * @param user Current user object
 * @returns true if transaction was created by current user
 */
export function isCreatedByCurrentUser(tx: InboxTransaction, user: User | null | undefined): boolean {
  if (!user) return false;

  // Check isOriginator flag (primary indicator)
  if (tx.isOriginator === true) {
    return true;
  }

  // Check various user ID fields
  if (
    tx.userID === user._id ||
    tx.userId === user._id ||
    tx.username === user.username ||
    tx.createdByCurrentUser === true ||
    tx.createdBy === user._id ||
    tx.createdBy === user.username ||
    tx.originator === user._id ||
    tx.originator === user.username
  ) {
    return true;
  }

  // Check nested data fields
  if (tx.data?.createdBy && (tx.data.createdBy === user._id || tx.data.createdBy === user.username)) {
    return true;
  }

  // Check nested payload fields
  if (tx.payload?.createdBy && (tx.payload.createdBy === user._id || tx.payload.createdBy === user.username)) {
    return true;
  }

  return false;
}

/**
 * Check if a transaction is a revealed token (cash token that is revealed but not redeemed)
 * @param tx Transaction to check
 * @param user Current user object
 * @returns true if transaction is a revealed token that user should see
 */
export function isRevealedToken(tx: InboxTransaction, user: User | null | undefined): boolean {
  // Must NOT be an invoice (invoices are handled separately)
  if (tx.isInvoice === true) {
    return false;
  }

  // Must be revealed status (not yet redeemed)
  const status = (tx.status || '').toString().trim().toUpperCase();
  if (status !== 'REVEALED') {
    return false;
  }

  // Exclude if already redeemed
  if (
    tx.isRedeemed === true ||
    status === 'REDEEMED' ||
    status === 'COMPLETED'
  ) {
    return false;
  }

  // Originator revealed tokens are not deposit tasks. Escrow release is a separate filter.
  if (isCreatedByCurrentUser(tx, user)) {
    return false;
  }

  // Held escrow: payee waits for issuer release; do not show Deposit yet.
  if (isEscrowed(tx) && !isEscrowReleased(tx)) {
    return false;
  }

  return true;
}

/**
 * Check if a transaction is an unpaid invoice
 * @param tx Transaction to check
 * @param user Current user object
 * @returns true if transaction is an unpaid invoice that user should see
 */
export function isUnpaidInvoice(tx: InboxTransaction, user: User | null | undefined): boolean {
  // Must be an invoice
  if (tx.isInvoice !== true) {
    return false;
  }

  // Exclude invoices that are already paid/completed
  const status = (tx.status || '').toString().trim().toUpperCase();
  const isPaid =
    status === 'COMPLETED' ||
    status === 'PAID' ||
    status === 'REDEEMED' ||
    tx.isRedeemed === true ||
    tx.isPaid === true;

  if (isPaid) {
    return false;
  }

  // Exclude invoices created by current user (user should only see invoices sent TO them)
  if (isCreatedByCurrentUser(tx, user)) {
    return false;
  }

  // Include invoices where user is the recipient
  return true;
}

/**
 * Filter transactions to get revealed tokens
 * @param transactions Array of transactions to filter
 * @param user Current user object
 * @returns Array of revealed token transactions
 */
export function filterRevealedTokens(
  transactions: InboxTransaction[],
  user: User | null | undefined
): InboxTransaction[] {
  return transactions.filter(tx => isRevealedToken(tx, user));
}

/**
 * Filter transactions to get unpaid invoices
 * @param transactions Array of transactions to filter
 * @param user Current user object
 * @returns Array of unpaid invoice transactions
 */
export function filterUnpaidInvoices(
  transactions: InboxTransaction[],
  user: User | null | undefined
): InboxTransaction[] {
  return transactions.filter(tx => isUnpaidInvoice(tx, user));
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function collectEscrowSources(tx: InboxTransaction): Record<string, unknown>[] {
  const sources: Record<string, unknown>[] = [tx as Record<string, unknown>];
  const payload = asRecord(tx.payload);
  const originator = asRecord(tx.originatorPayload);
  const data = asRecord(tx.data);
  const nestedPayload = asRecord(data?.payload);
  if (payload) sources.push(payload);
  if (originator) sources.push(originator);
  if (data) sources.push(data);
  if (nestedPayload) sources.push(nestedPayload);
  return sources;
}

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

function tokenStatus(tx: InboxTransaction): string {
  return (tx.status || '').toString().trim().toUpperCase();
}

function isEscrowed(tx: InboxTransaction): boolean {
  return collectEscrowSources(tx).some(
    (source) =>
      isTruthyFlag(source.escrowRequired) ||
      isTruthyFlag(source.escrow) ||
      source.escrowSettings != null ||
      Boolean(source.escrowId)
  );
}

function isEscrowReleased(tx: InboxTransaction): boolean {
  return collectEscrowSources(tx).some((source) => {
    const status = String(source.escrowStatus ?? '').toUpperCase();
    return isTruthyFlag(source.escrowReleased) || status === 'RELEASED' || status === 'REFUNDED';
  });
}

/**
 * Inbox Release escrow: current user originated the token, it is escrowed, status is revealed, not released.
 * Revealed tokens do not create this task for payees or for non-escrowed tokens.
 */
export function isEscrowAwaitingIssuerRelease(
  tx: InboxTransaction,
  user: User | null | undefined
): boolean {
  if (tx.isInvoice === true) {
    return false;
  }

  if (!isCreatedByCurrentUser(tx, user)) {
    return false;
  }

  if (tokenStatus(tx) !== 'REVEALED') {
    return false;
  }

  if (!isEscrowed(tx) || isEscrowReleased(tx)) {
    return false;
  }

  return true;
}

export function filterEscrowedTokensAwaitingRelease(
  transactions: InboxTransaction[],
  user: User | null | undefined
): InboxTransaction[] {
  return transactions.filter(tx => isEscrowAwaitingIssuerRelease(tx, user));
}
